import React, { useEffect, useState } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../PageHeader";
import axios from "axios";
import Cookies from "js-cookie";
import { triggerAlert } from "../../utils/commonFunctions";
import Loader from "../../common/Loader";

/**
 * Settings > Branding
 *
 * White-label control panel. Everything saved here flows to both portals
 * (via public /api/branding/) and to outbound emails / invoice PDFs
 * (via apps/core/branding.py:get_branding()).
 *
 * Phase 1 ships text + colors. Image uploads (logo, favicon, login
 * banner) are placeholders for Phase 2 — the form shows the slots so
 * reseller-facing layout is final, but the upload widgets are
 * disabled with a "Coming soon" caption.
 *
 * Saving with an empty field clears it back to the deployment's
 * .env / hard-coded default (the backend treats blank as "fall back").
 */

const DEFAULT_FORM = {
  app_name: "",
  app_tagline: "",
  company_legal_name: "",
  primary_color: "",
  primary_color_dark: "",
  support_email: "",
  ticket_notification_emails: "",
  support_url: "",
  company_url: "",
  terms_url: "",
  privacy_url: "",
  from_email: "",
  from_name: "",
  reply_to_email: "",
  footer_text: "",
  smtp_host: "",
  smtp_port: "",
  smtp_user: "",
  smtp_password: "",
  smtp_use_tls: true,
  logo_primary_url: "",
  logo_square_url: "",
  favicon_url: "",
  login_banner_url: "",
};

// Sentinel the backend returns in place of the stored password so the
// form can show "password set" without ever receiving the real value.
// Submitting this back is a no-op (preserves the stored password).
const MASKED_PASSWORD = "__BRANDING_PASSWORD_SET__";

// Suggested asset dimensions so resellers know what to upload. These
// hints are also surfaced in the Phase-2 upload widget captions.
const IMAGE_HINTS = {
  logo_primary: "Horizontal PNG, transparent, ~300 × 80 px",
  logo_square: "Square PNG, transparent, ~256 × 256 px (also used for favicon)",
  favicon: "32 × 32 or 64 × 64 px ICO/PNG",
  login_banner: "JPG/PNG, ~1200 × 600 px",
};

export default function Branding() {
  document.title = "Branding Settings";
  const mainHeading = "Branding";
  const heading = "Settings / Branding";

  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${Cookies.get("admin_user_token")}`,
      "Content-Type": "application/json",
    },
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get("/api/admin/branding/", authHeaders())
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        // Pre-fill the form with whatever the backend returns; empty
        // string is the canonical "not set, fall back" value so don't
        // default to placeholders here. Booleans and numbers keep
        // their native type so the bool toggle and port input behave.
        const next = { ...DEFAULT_FORM };
        Object.keys(DEFAULT_FORM).forEach((k) => {
          if (data[k] === undefined || data[k] === null) return;
          if (typeof DEFAULT_FORM[k] === "boolean") {
            next[k] = !!data[k];
          } else if (k === "smtp_port") {
            next[k] = data[k] === "" ? "" : String(data[k]);
          } else {
            next[k] = String(data[k]);
          }
        });
        setForm(next);
      })
      .catch(() => {
        triggerAlert("error", "Oops...", "Failed to load branding settings.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const updateBool = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: !!e.target.checked }));

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put("/api/admin/branding/", form, authHeaders());
      const changed = res.data?.updated_fields || [];
      if (changed.length) {
        triggerAlert(
          "success",
          "",
          `Saved ${changed.length} change${changed.length === 1 ? "" : "s"}. Reload portals to see the new brand.`
        );
      } else {
        triggerAlert("info", "", "No changes detected.");
      }
    } catch (err) {
      triggerAlert("error", "Oops...", "Failed to save branding settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="page-content" style={{ backgroundColor: "#ededed" }}>
        <div className="main-content">
          <PageHeader mainHeading={mainHeading} heading={heading} />
          <div className="container-fluid">
            {loading ? (
              <div className="loader-overlay text-white">
                <Loader />
              </div>
            ) : (
              <form onSubmit={handleSave}>
                {/* ── Identity ─────────────────────────────────────── */}
                <Card title="Identity" icon="bi-fonts">
                  <Row>
                    <TextCol label="App name" placeholder="Digital Employees" value={form.app_name} onChange={update("app_name")} help="Browser tab title, sidebar header, email subjects. Falls back to 'Digital Employees' if blank." />
                    <TextCol label="Tagline" placeholder="Optional one-liner under app name on login" value={form.app_tagline} onChange={update("app_tagline")} />
                  </Row>
                  <Row>
                    <TextCol label="Company legal name" placeholder="Acme Inc." value={form.company_legal_name} onChange={update("company_legal_name")} help="Used in invoice PDF footer and the Terms page copyright line." />
                  </Row>
                </Card>

                {/* ── Theme colors ─────────────────────────────────── */}
                <Card title="Theme colors" icon="bi-palette-fill">
                  <Row>
                    <ColorCol label="Primary color" value={form.primary_color} fallback="#1e40af" onChange={update("primary_color")} help="Buttons, links, accents." />
                    <ColorCol label="Primary (dark)" value={form.primary_color_dark} fallback="#1e3a8a" onChange={update("primary_color_dark")} help="Hover / pressed state. Defaults to primary if blank." />
                  </Row>
                </Card>

                {/* ── Contact + URLs ───────────────────────────────── */}
                <Card title="Contact & URLs" icon="bi-link-45deg">
                  <Row>
                    <TextCol label="Support email" placeholder="support@acme.com" value={form.support_email} onChange={update("support_email")} help="Shown on support tickets, error pages, the customer's billing footer." />
                    <TextCol label="Support / help docs URL" placeholder="https://docs.acme.com" value={form.support_url} onChange={update("support_url")} />
                  </Row>
                  <Row>
                    <div className="col-md-12">
                      <label className="form-label fw-semibold">
                        Ticket-creation notifications
                      </label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="ops@acme.com, on-call@acme.com"
                        value={form.ticket_notification_emails}
                        onChange={update("ticket_notification_emails")}
                      />
                      <div className="form-text">
                        Comma-separated. Every address here is BCC'd whenever a
                        customer creates a support ticket — not on replies or
                        status updates. Kept as BCC so customers never see the
                        internal recipient list. Leave blank to notify only
                        the deployment's default SUPPORT_MAIL.
                      </div>
                    </div>
                  </Row>
                  <Row>
                    <TextCol label="Company website" placeholder="https://acme.com" value={form.company_url} onChange={update("company_url")} />
                    <TextCol label="Terms URL" placeholder="https://acme.com/terms (blank uses built-in page)" value={form.terms_url} onChange={update("terms_url")} />
                  </Row>
                  <Row>
                    <TextCol label="Privacy URL" placeholder="https://acme.com/privacy" value={form.privacy_url} onChange={update("privacy_url")} />
                  </Row>
                </Card>

                {/* ── Email identity ───────────────────────────────── */}
                <Card title="Email identity" icon="bi-envelope-fill">
                  <Row>
                    <TextCol label="From email" placeholder="noreply@acme.com" value={form.from_email} onChange={update("from_email")} help="Outbound From address. Blank = use deployment default." />
                    <TextCol label="From name" placeholder="Acme Support" value={form.from_name} onChange={update("from_name")} help="Display name on outbound emails. Blank = use app name." />
                  </Row>
                  <Row>
                    <TextCol label="Reply-To email" placeholder="reply@acme.com" value={form.reply_to_email} onChange={update("reply_to_email")} help="Blank = use support email." />
                  </Row>
                </Card>

                {/* ── SMTP override ────────────────────────────────── */}
                <Card title="Mail server (SMTP)" icon="bi-hdd-network">
                  <p className="text-muted small mb-3">
                    Optional — leave any field blank to use the deployment's
                    default values from <code>.env</code>. Filling these in
                    routes every outbound email (welcome, invoices, support
                    tickets) through your own SMTP server.
                  </p>
                  <Row>
                    <TextCol label="SMTP host" placeholder="mail.acme.com" value={form.smtp_host} onChange={update("smtp_host")} help="Hostname of the SMTP server." />
                    <TextCol label="Port" placeholder="587" value={form.smtp_port} onChange={update("smtp_port")} help="587 = STARTTLS (recommended). 465 = SSL." />
                  </Row>
                  <Row>
                    <TextCol label="Username" placeholder="noreply@acme.com" value={form.smtp_user} onChange={update("smtp_user")} help="SMTP auth user, usually the From address." />
                    <PasswordCol
                      label="Password"
                      value={form.smtp_password}
                      onChange={update("smtp_password")}
                      maskedSentinel={MASKED_PASSWORD}
                      onClear={() => setForm((prev) => ({ ...prev, smtp_password: "" }))}
                      help="Leave the masked field as-is to keep the stored password. Empty = clear and fall back to .env."
                    />
                  </Row>
                  <Row>
                    <div className="col-md-6">
                      <div className="form-check form-switch mt-4">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="smtp_use_tls"
                          checked={!!form.smtp_use_tls}
                          onChange={updateBool("smtp_use_tls")}
                          style={{ cursor: "pointer", width: "2.5rem", height: "1.25rem" }}
                        />
                        <label className="form-check-label fw-semibold ms-2" htmlFor="smtp_use_tls">
                          Use STARTTLS
                        </label>
                        <div className="text-muted small mt-1">
                          Strongly recommended — most modern relays reject plain TCP.
                        </div>
                      </div>
                    </div>
                  </Row>
                </Card>

                {/* ── Footer ───────────────────────────────────────── */}
                <Card title="Footer" icon="bi-text-paragraph">
                  <Row>
                    <TextCol label="Footer text" placeholder="© {year} Acme Inc. All rights reserved." value={form.footer_text} onChange={update("footer_text")} help="Use {year} for the current year. Shown in portal + email footers." />
                  </Row>
                </Card>

                {/* ── Image uploads (Phase 2) ─────────────────────── */}
                <Card title="Logos & images" icon="bi-image-fill">
                  <p className="text-muted small mb-3">
                    Upload images directly — they're saved on the server and the URL is written to BrandingSettings. Max 2 MB each. PNG / JPG / WebP / SVG / ICO.
                  </p>
                  <ImageUpload
                    slot="logo_primary"
                    label="Primary logo"
                    hint={IMAGE_HINTS.logo_primary}
                    currentUrl={form.logo_primary_url}
                    onChange={(url) => setForm((prev) => ({ ...prev, logo_primary_url: url }))}
                  />
                  <ImageUpload
                    slot="logo_square"
                    label="Square / favicon source"
                    hint={IMAGE_HINTS.logo_square}
                    currentUrl={form.logo_square_url}
                    onChange={(url) => setForm((prev) => ({ ...prev, logo_square_url: url }))}
                  />
                  <ImageUpload
                    slot="favicon"
                    label="Favicon override"
                    hint={IMAGE_HINTS.favicon}
                    currentUrl={form.favicon_url}
                    onChange={(url) => setForm((prev) => ({ ...prev, favicon_url: url }))}
                  />
                  <ImageUpload
                    slot="login_banner"
                    label="Login screen banner"
                    hint={IMAGE_HINTS.login_banner}
                    currentUrl={form.login_banner_url}
                    onChange={(url) => setForm((prev) => ({ ...prev, login_banner_url: url }))}
                  />
                </Card>

                <div className="d-flex justify-content-end gap-2 mb-4">
                  <button type="submit" className="btn btn-primary d-flex align-items-center gap-2" disabled={saving} style={{ padding: "10px 24px", borderRadius: "8px", fontWeight: 500, border: "none" }}>
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg"></i>
                        Save branding
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tiny presentational helpers — kept in-file because they're only
// used by this page and don't deserve a shared module yet. ────────────

function Card({ title, icon, disabledBadge, children }) {
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0 fw-bold" style={{ color: "#014670" }}>
          {icon && <i className={`bi ${icon} me-2`} />}
          {title}
        </h5>
        {disabledBadge && (
          <span className="badge bg-secondary-subtle text-secondary">{disabledBadge}</span>
        )}
      </div>
      <div className="card-body p-4">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="row">{children}</div>;
}

function TextCol({ label, value, onChange, placeholder, help }) {
  return (
    <div className="col-md-6">
      <div className="mb-3">
        <label className="form-label fw-semibold">{label}</label>
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={value || ""}
          onChange={onChange}
          style={{ borderRadius: "8px", padding: "10px 14px", fontSize: "14px" }}
        />
        {help && <small className="text-muted d-block mt-1">{help}</small>}
      </div>
    </div>
  );
}

function ColorCol({ label, value, fallback, onChange, help }) {
  const swatch = value || fallback;
  return (
    <div className="col-md-6">
      <div className="mb-3">
        <label className="form-label fw-semibold">{label}</label>
        <div className="d-flex align-items-center gap-2">
          <input
            type="color"
            value={swatch}
            onChange={onChange}
            style={{ width: 48, height: 40, border: "1px solid #dee2e6", borderRadius: 8, padding: 2, cursor: "pointer" }}
          />
          <input
            type="text"
            className="form-control"
            placeholder={fallback}
            value={value || ""}
            onChange={onChange}
            style={{ borderRadius: "8px", padding: "10px 14px", fontSize: "14px", fontFamily: "monospace" }}
          />
        </div>
        {help && <small className="text-muted d-block mt-1">{help}</small>}
      </div>
    </div>
  );
}

function PasswordCol({ label, value, onChange, maskedSentinel, onClear, help }) {
  const isMasked = value === maskedSentinel;
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="col-md-6">
      <div className="mb-3">
        <label className="form-label fw-semibold">{label}</label>
        <div className="input-group">
          <input
            type={revealed && !isMasked ? "text" : "password"}
            className="form-control"
            placeholder={isMasked ? "•••••••• (stored — leave to keep)" : "Enter SMTP password"}
            value={isMasked ? "" : value || ""}
            onChange={onChange}
            style={{ borderRadius: "8px 0 0 8px", padding: "10px 14px", fontSize: "14px" }}
          />
          {!isMasked && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setRevealed((v) => !v)}
              title={revealed ? "Hide" : "Show"}
              style={{ borderRadius: 0 }}
            >
              <i className={`bi ${revealed ? "bi-eye-slash" : "bi-eye"}`} />
            </button>
          )}
          {isMasked && (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={onClear}
              title="Clear stored password"
              style={{ borderRadius: "0 8px 8px 0" }}
            >
              <i className="bi bi-x-lg" />
            </button>
          )}
        </div>
        {help && <small className="text-muted d-block mt-1">{help}</small>}
      </div>
    </div>
  );
}

function ImageUpload({ slot, label, hint, currentUrl, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      triggerAlert("error", "Too large", "Max upload size is 2 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `/api/admin/branding/upload/?slot=${encodeURIComponent(slot)}`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("admin_user_token")}`,
            // Let the browser set the multipart boundary.
          },
        }
      );
      onChange(res.data?.url || "");
      triggerAlert("success", "", "Image uploaded.");
    } catch (err) {
      const msg = err?.response?.data?.error || "Upload failed.";
      triggerAlert("error", "", msg);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    setUploading(true);
    try {
      const res = await axios.delete(
        `/api/admin/branding/upload/?slot=${encodeURIComponent(slot)}`,
        {
          headers: { Authorization: `Bearer ${Cookies.get("admin_user_token")}` },
        }
      );
      onChange(res.data?.url || "");
      triggerAlert("success", "", "Image cleared.");
    } catch (err) {
      triggerAlert("error", "", "Could not clear image.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="d-flex align-items-center gap-3 mb-3 p-3" style={{ background: "#f8f9fa", borderRadius: 8, border: "1px dashed #ced4da" }}>
      <div className="flex-shrink-0" style={{ width: 64, height: 64, background: "#fff", borderRadius: 8, border: "1px solid #e9ecef", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {currentUrl ? (
          <img src={currentUrl} alt={label} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        ) : (
          <i className="bi bi-image" style={{ fontSize: 26, color: "#adb5bd" }} />
        )}
      </div>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-semibold" style={{ color: "#495057" }}>{label}</div>
        <small className="text-muted d-block">{hint}</small>
        {currentUrl && (
          <div className="text-truncate small mt-1" style={{ color: "#6c757d", maxWidth: "100%" }} title={currentUrl}>
            {currentUrl}
          </div>
        )}
      </div>
      <div className="d-flex flex-column gap-1 flex-shrink-0">
        <label className="btn btn-sm btn-primary d-flex align-items-center gap-1 mb-0" style={{ cursor: uploading ? "wait" : "pointer", borderRadius: 6 }}>
          <i className="bi bi-upload" />
          {uploading ? "Uploading…" : (currentUrl ? "Replace" : "Upload")}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon" hidden disabled={uploading} onChange={handleFile} />
        </label>
        {currentUrl && (
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleClear} disabled={uploading} style={{ borderRadius: 6 }}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
